/**
 * Generic animation controller for sections that follow the pattern:
 *   1. Header (title + description) fades in + slides up
 *   2. Content items appear afterwards (staggered or simultaneous)
 *
 * Usage in Liquid:
 *   - Section wrapper:  data-content-animate  data-stagger="true|false"
 *   - Header elements:  data-animate-header
 *   - Content items:    data-animate-item
 *
 * Animations fire only once (observer unobserves after trigger).
 * Respects prefers-reduced-motion.
 *
 * NOTE: This script may be loaded by multiple sections on the same page.
 * A global guard prevents duplicate initialisation, and a per-element
 * data attribute prevents double-observing.
 */
(function () {
  'use strict';

  // Global guard: only run init() once even if the script is loaded multiple times
  if (window.__contentAnimationInitialised) return;
  window.__contentAnimationInitialised = true;

  /** Delay (ms) before header elements start appearing */
  var HEADER_DELAY = 100;

  /** Delay (ms) between header becoming visible and first content item */
  var CONTENT_START_DELAY = 500;

  /** Delay (ms) between each content item when stagger is enabled */
  var ITEM_STAGGER = 200;

  var VISIBLE_CLASS = 'content-animate--visible';
  var INIT_FLAG = 'data-content-animate-init';

  /**
   * Initialise animation for a single section.
   * @param {HTMLElement} section – element with [data-content-animate]
   */
  function initContentAnimation(section) {
    // Skip if already initialised (prevents double-observing)
    if (section.hasAttribute(INIT_FLAG)) return;
    section.setAttribute(INIT_FLAG, '');

    var headers = Array.from(section.querySelectorAll('[data-animate-header]'));
    var items = Array.from(section.querySelectorAll('[data-animate-item]'));

    if (headers.length === 0 && items.length === 0) return;

    var stagger = section.getAttribute('data-stagger') !== 'false';

    // If user prefers reduced motion, show everything immediately
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      showAll(headers, items);
      return;
    }

    var hasAnimated = false;

    var observer = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          if (entry.isIntersecting && !hasAnimated) {
            hasAnimated = true;
            observer.unobserve(section);
            runAnimation(headers, items, stagger);
          }
        });
      },
      {
        rootMargin: '0px 0px -50px 0px',
        threshold: 0.1,
      }
    );

    observer.observe(section);
  }

  /**
   * Run the sequential animation.
   */
  function runAnimation(headers, items, stagger) {
    // Step 1: Reveal header elements (title, description, topline)
    headers.forEach(function (el, i) {
      setTimeout(function () {
        el.classList.add(VISIBLE_CLASS);
      }, HEADER_DELAY + i * 100);
    });

    // Step 2: Reveal content items after header
    var headerDuration = HEADER_DELAY + headers.length * 100;
    var contentStart = headerDuration + CONTENT_START_DELAY;

    items.forEach(function (el, i) {
      var delay = stagger ? contentStart + i * ITEM_STAGGER : contentStart;
      setTimeout(function () {
        el.classList.add(VISIBLE_CLASS);
      }, delay);
    });
  }

  /**
   * Show all elements immediately (reduced motion / design mode).
   */
  function showAll(headers, items) {
    headers.forEach(function (el) {
      el.classList.add(VISIBLE_CLASS);
    });
    items.forEach(function (el) {
      el.classList.add(VISIBLE_CLASS);
    });
  }

  /**
   * Initialise all content-animate sections on the page.
   */
  function init() {
    var sections = document.querySelectorAll('[data-content-animate]');
    sections.forEach(initContentAnimation);
  }

  // Run on DOMContentLoaded
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // Shopify theme editor: re-initialise when sections are added / reloaded
  if (window.Shopify && Shopify.designMode) {
    document.addEventListener('shopify:section:load', function (event) {
      var section = event.target.querySelector('[data-content-animate]');
      if (section) {
        // Remove init flag so it can be re-initialised in design mode
        section.removeAttribute(INIT_FLAG);
        var headers = Array.from(section.querySelectorAll('[data-animate-header]'));
        var items = Array.from(section.querySelectorAll('[data-animate-item]'));
        showAll(headers, items);
      }
    });
  }
})();
